<div class="header-container">
  <div class="header">
    <span class="title">{{
      'tlc.positions.position_details_dialog.tabs.skills.header.title' | translate
    }}</span>
    <span>{{
      'tlc.positions.position_details_dialog.tabs.skills.header.subtitle' | translate
    }}</span>
  </div>
  <tlc-add-skills-dropdown
    [skills]="availableSkills()"
    [skillMappings]="availableSkillMappings()"
    [positionName]="positionData()?.name ?? ''"
    [loading]="isAssigning()"
    (skillSelected)="onSkillSelected($event)"
    (skillMappingSelected)="onSkillMappingSelected($event)"
  />
</div>
<div class="content">
  @if (positionSkillEntity() === null) {
    <div class="skills-skeleton">
      @for (_ of [1, 2, 3]; track $index) {
        <div class="skeleton-info">
          <rt-placeloader variant="text" />
          <rt-placeloader variant="text" [short]="true" />
        </div>
        <div class="skeleton-proficiency">
          <rt-placeloader variant="text" />
          <rt-placeloader variant="text" [short]="true" />
        </div>
        <div></div>
      }
    </div>
  } @else {
    @if (assignedSkills().length > 0) {
      <div class="skills-table">
        <span class="column-label">{{
          'tlc.positions.position_details_dialog.tabs.skills.list.skills.column.skill_name'
            | translate
        }}</span>
        <span class="column-label">{{
          'tlc.positions.position_details_dialog.tabs.skills.list.skills.column.proficiency_level'
            | translate
        }}</span>
        <span class="column-label"></span>

        @for (item of assignedSkills(); track item.skill.id) {
          <div class="skill-info">
            <span class="skill-name">{{ item.skill.name }}</span>
            @if (item.skill.descriptionText) {
              <span class="skill-description">{{ item.skill.descriptionText }}</span>
            }
            @if (item.skill.group) {
              <span class="skill-group-tag">{{ item.skill.group.name }}</span>
            }
          </div>
          <div class="proficiency-cell">
            @if (item.proficiency) {
              <div class="proficiency-content">
                <div class="proficiency-level">
                  <div class="proficiency-dots">
                    @for (dot of item.skill.proficiencies ?? []; track dot.id) {
                      <span
                        class="dot"
                        [class.filled]="dot.position <= item.proficiency.position"
                      ></span>
                    }
                  </div>
                  <span class="proficiency-name">{{ item.proficiency.name }}</span>
                </div>
                @if (item.proficiency.descriptionText) {
                  <span class="proficiency-description">{{
                    item.proficiency.descriptionText
                  }}</span>
                }
              </div>
            }
          </div>
          <div class="skill-actions">
            <rt-icon-button
              #editOrigin="rtOverlayOrigin"
              rt-overlay-origin
              variant="plain"
              iconName="edit"
              shape="square"
              size="small"
              [class.pressed]="editTmpl.open"
              [loading]="updatingSkillId() === item.skill.id"
              (click)="editTmpl.openAt(editOrigin)"
            />
            <ng-template
              #editTmpl="rtConnectedOverlay"
              rt-connected-overlay
              [width]="DropdownWidth.Medium"
              [hasBackdrop]="true"
              (backdropClick)="editTmpl.open = false"
            >
              @if (getAvailableSkill(item.skill.id); as editSkill) {
                <rt-dropdown>
                  <tlc-proficiency-panel
                    [skill]="editSkill"
                    [positionName]="positionData()?.name ?? ''"
                    (proficiencySelected)="
                      editTmpl.open = false; onUpdateSkillProficiency(item, $event)
                    "
                  />
                </rt-dropdown>
              }
            </ng-template>
            <rt-icon-button
              variant="plain"
              iconName="close"
              shape="square"
              size="small"
              [loading]="removingSkillId() === item.skill.id"
              (click)="onRemoveSkill(item)"
            />
          </div>
        }
      </div>
    }

    @for (mappingItem of assignedSkillMappings(); track mappingItem.id) {
      <div class="skill-mapping-section">
        <div class="skill-mapping-header">
          <div class="skill-mapping-header-info">
            <rt-badge
              color="secondary"
              [text]="
                'tlc.positions.position_details_dialog.tabs.skills.list.skill_mapping.label'
                  | translate
              "
            />
            <span class="skill-mapping-title">
              {{ mappingItem.name }}
              @if (mappingItem.levels?.[0]) {
                &bull; {{ mappingItem.levels?.[0]?.name }}
              }
            </span>
          </div>
          <div class="skill-mapping-actions">
            <rt-button variant="tertiary" size="small">
              <rt-icon prefix name="open-in-new" />
              <span>{{
                'tlc.positions.position_details_dialog.tabs.skills.list.skill_mapping.manage_skill_mapping'
                  | translate
              }}</span>
            </rt-button>
            <rt-icon-button
              #moreOrigin="rtOverlayOrigin"
              rt-overlay-origin
              variant="tertiary"
              iconName="more-horiz"
              shape="square"
              size="small"
              [class.pressed]="moreTmpl.open"
              [loading]="removingSkillMappingId() === mappingItem.id"
              (click)="moreTmpl.openAt(moreOrigin)"
            />
            <ng-template
              #moreTmpl="rtConnectedOverlay"
              rt-connected-overlay
              [width]="DropdownWidth.Medium"
              [hasBackdrop]="true"
              (backdropClick)="moreTmpl.open = false"
            >
              <rt-dropdown>
                <rt-dropdown-item
                  (click)="moreTmpl.open = false; onRemoveSkillMapping(mappingItem)"
                >
                  <rt-icon name="close" prefix />
                  <span>{{
                    'tlc.positions.position_details_dialog.tabs.skills.list.skill_mapping.unassign'
                      | translate
                  }}</span>
                </rt-dropdown-item>
              </rt-dropdown>
            </ng-template>
          </div>
        </div>

        @if (mappingItem.skills.length > 0) {
          <div class="skills-table">
            @for (item of mappingItem.skills; track item.skillId) {
              <div class="skill-info">
                <span class="skill-name">{{ item.name }}</span>
                @if (item.descriptionText) {
                  <span class="skill-description">{{ item.descriptionText }}</span>
                }
                @if (item.group) {
                  <span class="skill-group-tag">{{ item.group }}</span>
                }
              </div>
              <div class="proficiency-cell">
                @if (getProficiencyForMappingSkill(mappingItem, item); as proficiency) {
                  <div class="proficiency-content">
                    <div class="proficiency-level">
                      <div class="proficiency-dots">
                        @for (dot of item.proficiencies ?? []; track dot.id) {
                          <span
                            class="dot"
                            [class.filled]="dot.position <= proficiency.position"
                          ></span>
                        }
                      </div>
                      <span class="proficiency-name">{{ proficiency.name }}</span>
                    </div>
                    @if (proficiency.descriptionText) {
                      <span class="proficiency-description">{{ proficiency.descriptionText }}</span>
                    }
                  </div>
                } @else {
                  <div class="proficiency-content">
                    <div class="proficiency-level">
                      <div class="proficiency-dots">
                        @for (dot of item.proficiencies ?? []; track dot.id) {
                          <span class="dot"></span>
                        }
                      </div>
                      <span class="proficiency-name">{{
                        'tlc.positions.position_details_dialog.tabs.skills.list.skill_mapping.not_required.label'
                          | translate
                      }}</span>
                    </div>
                    <span class="proficiency-description">{{
                      'tlc.positions.position_details_dialog.tabs.skills.list.skill_mapping.not_required.description'
                        | translate
                    }}</span>
                  </div>
                }
              </div>
              <div class="skill-actions-placeholder" aria-hidden="true">
                <rt-icon-button variant="plain" iconName="edit" shape="square" size="small" />
                <rt-icon-button variant="plain" iconName="close" shape="square" size="small" />
              </div>
            }
          </div>
        }
      </div>
    }
    @if (assignedSkills().length === 0 && assignedSkillMappings().length === 0) {
      <rt-empty-state
        icon="positions"
        [title]="
          'tlc.positions.position_details_dialog.tabs.skills.header.empty_state.title' | translate
        "
        [desc]="
          'tlc.positions.position_details_dialog.tabs.skills.header.empty_state.subtitle'
            | translate
        "
      />
    }
  }
</div>
